import React from 'react';
import { ChevronDown, ChevronUp, Clock3, PackageSearch, Phone, Search, User, Users, X } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const getApiPath = (path) => {
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const response = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error || `Request failed: ${response.status}`);
  return data;
};

const statusClass = (status) => {
  const classes = {
    'В обработке': 'border-orange-400/30 bg-orange-400/10 text-orange-200',
    'Поступил в Токио': 'border-violet-400/30 bg-violet-400/10 text-violet-200',
    'В Пути': 'border-blue-400/30 bg-blue-400/10 text-blue-200',
    'Готов к выдаче': 'border-yellow-400/30 bg-yellow-400/10 text-yellow-200',
    'Получен': 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200',
  };
  return classes[status] || 'border-white/15 bg-white/5 text-text-secondary';
};

const displayValue = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') return '—';
  return `${value}${suffix}`;
};

const parsePositiveAmount = (value) => {
  const amount = Number(String(value || '').replace(/\s+/g, '').replace(',', '.'));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

const requiresNewLocalAmount = (order) => {
  const som = parsePositiveAmount(order?.amount_som);
  const tenge = parsePositiveAmount(order?.amount_tenge);
  return String(order?.status || '').trim() === 'Готов к выдаче' || !!som || !!tenge;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ru-RU', {
    timeZone: 'Asia/Bishkek',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ManagerOrderSearchPage = () => {
  const { initData } = useTelegram();
  const [query, setQuery] = React.useState('');
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [searched, setSearched] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState({});
  const [historyOpen, setHistoryOpen] = React.useState({});
  const [historyByOrder, setHistoryByOrder] = React.useState({});
  const [historyLoadingId, setHistoryLoadingId] = React.useState(null);
  const [ownerEditorId, setOwnerEditorId] = React.useState(null);
  const [ownerQuery, setOwnerQuery] = React.useState('');
  const [ownerCandidates, setOwnerCandidates] = React.useState([]);
  const [ownerLoading, setOwnerLoading] = React.useState(false);
  const [ownerError, setOwnerError] = React.useState('');
  const [ownerSearched, setOwnerSearched] = React.useState(false);
  const [ownerAmounts, setOwnerAmounts] = React.useState({});
  const [ownerHistoryOpen, setOwnerHistoryOpen] = React.useState({});
  const [ownerHistoryByOrder, setOwnerHistoryByOrder] = React.useState({});
  const [ownerHistoryLoadingId, setOwnerHistoryLoadingId] = React.useState(null);

  const initDataRaw = initData?.initData;

  const searchOrders = async (event) => {
    event?.preventDefault();
    const value = query.trim();
    if (value.length < 3 || !initDataRaw) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiPost('/api/manager/orders/search', { initData: initDataRaw, query: value });
      setOrders(Array.isArray(result?.orders) ? result.orders : []);
      setSearched(true);
      setDetailsOpen({});
      setHistoryOpen({});
    } catch (err) {
      setError(err?.message || 'Не удалось найти заказы');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setOrders([]);
    setSearched(false);
    setError('');
    setDetailsOpen({});
    setHistoryOpen({});
    setOwnerEditorId(null);
    setOwnerHistoryOpen({});
  };

  const openOwnerEditor = (orderId) => {
    setOwnerEditorId((current) => current === orderId ? null : orderId);
    setOwnerQuery('');
    setOwnerCandidates([]);
    setOwnerError('');
    setOwnerSearched(false);
    setOwnerAmounts({});
  };

  const searchOwnerCandidates = async (event, orderId) => {
    event?.preventDefault();
    const value = ownerQuery.trim();
    if (value.length < 3 || !initDataRaw) return;
    setOwnerLoading(true);
    setOwnerError('');
    setOwnerSearched(false);
    try {
      const result = await apiPost('/api/manager/orders/owner-candidates', {
        initData: initDataRaw,
        orderId,
        query: value,
      });
      setOwnerCandidates(Array.isArray(result?.users) ? result.users : []);
      setOwnerSearched(true);
    } catch (err) {
      setOwnerError(err?.message || 'Не удалось найти клиента');
    } finally {
      setOwnerLoading(false);
    }
  };

  const changeOwner = async (order, candidate) => {
    const candidateName = candidate.full_name
      || candidate.nickname
      || [candidate.first_name, candidate.last_name].filter(Boolean).join(' ')
      || candidate.phone
      || candidate.telegram_id;
    const orderCountry = String(order.country || '').trim().toUpperCase();
    const candidateCountry = String(candidate.country || '').trim().toUpperCase();
    const countryChanged = ['KG', 'KZ'].includes(candidateCountry) && candidateCountry !== orderCountry;
    const newLocalAmount = countryChanged ? parsePositiveAmount(ownerAmounts[candidate.telegram_id]) : null;
    const amountRequired = countryChanged && requiresNewLocalAmount(order);
    if (amountRequired && !newLocalAmount) {
      setOwnerError(`Укажите сумму в ${candidateCountry === 'KZ' ? 'тенге' : 'сомах'} для новой страны`);
      return;
    }
    const countryWarning = countryChanged
      ? `\nСтрана заказа изменится: ${orderCountry} → ${candidateCountry}.${newLocalAmount ? ` Новая сумма: ${newLocalAmount} ${candidateCountry === 'KZ' ? 'тенге' : 'сом'}.` : ' Сумма будет указана позже на стадии готовности к выдаче.'}`
      : '';
    const confirmed = window.confirm(`Перенести трек ${order.track_number} клиенту ${candidateName}?${countryWarning}`);
    if (!confirmed) return;
    setOwnerLoading(true);
    setOwnerError('');
    try {
      const result = await apiPost('/api/manager/orders/change-owner', {
        initData: initDataRaw,
        orderId: order.id,
        targetTelegramId: candidate.telegram_id,
        newLocalAmount,
      });
      setOrders((current) => current.map((item) => item.id === order.id ? { ...item, ...result.order } : item));
      setOwnerHistoryByOrder((current) => {
        const next = { ...current };
        delete next[order.id];
        return next;
      });
      setOwnerHistoryOpen((current) => ({ ...current, [order.id]: false }));
      setOwnerEditorId(null);
      setOwnerCandidates([]);
      setOwnerQuery('');
      setOwnerSearched(false);
      setOwnerAmounts({});
    } catch (err) {
      setOwnerError(err?.message || 'Не удалось изменить владельца');
    } finally {
      setOwnerLoading(false);
    }
  };

  const toggleOwnerHistory = async (orderId) => {
    if (ownerHistoryOpen[orderId]) {
      setOwnerHistoryOpen((current) => ({ ...current, [orderId]: false }));
      return;
    }
    setOwnerHistoryOpen((current) => ({ ...current, [orderId]: true }));
    if (ownerHistoryByOrder[orderId]) return;
    setOwnerHistoryLoadingId(orderId);
    setError('');
    try {
      const result = await apiPost('/api/manager/orders/owner-history', { initData: initDataRaw, orderId });
      setOwnerHistoryByOrder((current) => ({ ...current, [orderId]: result?.history || [] }));
    } catch (err) {
      setError(err?.message || 'Не удалось загрузить историю владельцев');
      setOwnerHistoryOpen((current) => ({ ...current, [orderId]: false }));
    } finally {
      setOwnerHistoryLoadingId(null);
    }
  };

  const toggleHistory = async (orderId) => {
    if (historyOpen[orderId]) {
      setHistoryOpen((current) => ({ ...current, [orderId]: false }));
      return;
    }
    setHistoryOpen((current) => ({ ...current, [orderId]: true }));
    if (historyByOrder[orderId]) return;
    setHistoryLoadingId(orderId);
    setError('');
    try {
      const result = await apiPost('/api/manager/orders/history', { initData: initDataRaw, orderId });
      setHistoryByOrder((current) => ({ ...current, [orderId]: result?.history || [] }));
    } catch (err) {
      setError(err?.message || 'Не удалось загрузить историю');
      setHistoryOpen((current) => ({ ...current, [orderId]: false }));
    } finally {
      setHistoryLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-bg pb-20 text-text-primary">
      <div className="mx-auto max-w-3xl px-4 pt-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-neon-yellow/25 bg-neon-yellow/10 text-neon-yellow">
            <PackageSearch size={22} />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Поиск заказов</h1>
            <p className="mt-1 text-sm text-text-secondary">Трек, телефон, ФИО или код выдачи.</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-xs leading-relaxed text-amber-100">
          Важно: владельца можно изменить только у неоплаченного и ещё не полученного заказа. Оплаченные и полученные заказы менять нельзя.
        </div>

        <form onSubmit={searchOrders} className="mt-5 rounded-2xl border border-stroke bg-surface p-4">
          <div className="relative">
            <Search size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Введите трек, телефон, ФИО или код"
              className="h-12 w-full rounded-2xl border border-stroke bg-[#161616] pl-11 pr-12 text-[16px] outline-none focus:border-neon-yellow/50"
            />
            {query ? (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-xl border border-stroke bg-white/5 text-text-secondary"
                aria-label="Очистить поиск"
              >
                <X size={15} />
              </button>
            ) : null}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="submit"
              disabled={loading || query.trim().length < 3 || !initDataRaw}
              className="h-11 rounded-2xl bg-neon-yellow text-sm font-bold text-black disabled:opacity-50"
            >
              {loading ? 'Ищем...' : 'Найти'}
            </button>
            <button
              type="button"
              onClick={clearSearch}
              disabled={!query && !searched}
              className="h-11 rounded-2xl border border-stroke bg-white/5 text-sm font-semibold text-text-primary disabled:opacity-40"
            >
              Очистить
            </button>
          </div>
        </form>

        {error ? <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-200">{error}</div> : null}
        {searched && !loading ? <div className="mt-4 text-xs text-text-muted">Найдено: {orders.length}</div> : null}

        <div className="mt-3 space-y-3">
          {orders.map((order) => {
            const detailsVisible = Boolean(detailsOpen[order.id]);
            const historyVisible = Boolean(historyOpen[order.id]);
            const history = historyByOrder[order.id] || [];
            const ownerHistoryVisible = Boolean(ownerHistoryOpen[order.id]);
            const ownerHistory = ownerHistoryByOrder[order.id] || [];
            const phone = String(order.phone || '').trim();
            const canChangeOwner = String(order.payment_status || '').trim() !== 'Оплачено'
              && String(order.status || '').trim() !== 'Получен';
            const paymentDisplay = ['Готов к выдаче', 'Получен'].includes(String(order.status || '').trim())
              ? (order.payment_status || '—')
              : 'Ещё не выставлена';
            const localAmount = order.country === 'KZ'
              ? displayValue(order.amount_tenge, ' тенге')
              : displayValue(order.amount_som, ' сом');
            return (
              <article key={order.id} className="rounded-2xl border border-stroke bg-surface p-4 shadow-[0_14px_45px_rgba(0,0,0,0.2)]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="break-all font-mono text-sm font-bold">{order.track_number}</div>
                    <div className="mt-2 flex items-center gap-2 text-sm text-text-secondary">
                      <User size={14} className="shrink-0" />
                      <span className="truncate">{order.recipient_name || 'ФИО не указано'}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
                      <Phone size={14} className="shrink-0" />
                      <span>{phone ? `+${phone.replace(/^\+/, '')}` : 'Телефон не указан'}</span>
                    </div>
                    <div className="mt-1 text-xs text-text-muted">Создан: {formatDateTime(order.created_at)}</div>
                  </div>
                  <span className={`shrink-0 rounded-full border px-3 py-1 text-[11px] font-semibold ${statusClass(order.status)}`}>
                    {order.status || '—'}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDetailsOpen((current) => ({ ...current, [order.id]: !detailsVisible }))}
                    className="flex h-10 items-center justify-center gap-2 rounded-xl border border-stroke bg-white/5 text-xs font-semibold"
                  >
                    {detailsVisible ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    {detailsVisible ? 'Скрыть детали' : 'Показать детали'}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleHistory(order.id)}
                    disabled={historyLoadingId === order.id}
                    className="flex h-10 items-center justify-center gap-2 rounded-xl border border-stroke bg-white/5 text-xs font-semibold disabled:opacity-50"
                  >
                    <Clock3 size={15} />
                    {historyLoadingId === order.id ? 'Загрузка...' : historyVisible ? 'Скрыть историю' : 'История статусов'}
                  </button>
                </div>

                {detailsVisible ? (
                  <div className="mt-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {[
                        ['Текущий статус', order.status || '—'],
                        ['Дата создания', formatDateTime(order.created_at)],
                        ['Последнее изменение', formatDateTime(order.updated_at)],
                        ['Количество', displayValue(order.quantity)],
                        ['Вес', displayValue(order.weight_kg, ' кг')],
                        ['Сумма', localAmount],
                        ['Сумма USD', displayValue(order.amount_usd, ' $')],
                        ['Оплата', paymentDisplay],
                        ['Код выдачи', order.pickup_code || '—'],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-xl border border-stroke bg-white/3 p-3">
                          <div className="text-[10px] uppercase tracking-wide text-text-muted">{label}</div>
                          <div className="mt-1 font-semibold">{value}</div>
                        </div>
                      ))}
                    </div>
                    {!['Готов к выдаче', 'Получен'].includes(String(order.status || '').trim()) ? (
                      <div className="mt-2 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-100">
                        Пометка: «Ожидает оплаты» до статуса «Готов к выдаче» является техническим значением. Оплата ещё не выставлена, сумма к оплате появится после подготовки заказа к выдаче.
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {historyVisible ? (
                  <div className="mt-3 rounded-xl border border-stroke bg-black/15 p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">История статусов</div>
                    {history.length ? (
                      <div className="mt-3 space-y-3">
                        {history.map((item) => (
                          <div key={item.id} className="flex items-start gap-3">
                            <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-neon-yellow" />
                            <div>
                              <div className="text-sm font-semibold">{item.status || '—'}</div>
                              <div className="mt-0.5 text-xs text-text-secondary">{formatDateTime(item.changed_at)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs leading-relaxed text-text-secondary">
                        История пока отсутствует. Заказ создан {formatDateTime(order.created_at)}.
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => openOwnerEditor(order.id)}
                    disabled={!canChangeOwner}
                    className="flex min-h-10 items-center justify-center gap-2 rounded-xl border border-stroke bg-white/5 px-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <Users size={15} />
                    {canChangeOwner ? 'Изменить владельца' : 'Перенос запрещён'}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleOwnerHistory(order.id)}
                    disabled={ownerHistoryLoadingId === order.id}
                    className="flex min-h-10 items-center justify-center gap-2 rounded-xl border border-stroke bg-white/5 px-2 text-xs font-semibold disabled:opacity-50"
                  >
                    <Clock3 size={15} />
                    {ownerHistoryLoadingId === order.id ? 'Загрузка...' : ownerHistoryVisible ? 'Скрыть переносы' : 'История владельца'}
                  </button>
                </div>
                {!canChangeOwner ? (
                  <div className="mt-2 text-[11px] text-text-muted">
                    Оплаченные и уже полученные заказы переносить нельзя.
                  </div>
                ) : null}

                {ownerHistoryVisible ? (
                  <div className="mt-3 rounded-xl border border-stroke bg-black/15 p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">История смены владельца</div>
                    {ownerHistory.length ? (
                      <div className="mt-3 space-y-3">
                        {ownerHistory.map((item) => {
                          const payload = item.payload || {};
                          return (
                            <div key={item.id} className="rounded-xl border border-stroke bg-white/3 p-3">
                              <div className="text-xs font-semibold">{formatDateTime(item.created_at)}</div>
                              <div className="mt-2 text-[11px] leading-relaxed text-text-secondary">
                                Было: {payload.oldRecipientName || 'Без ФИО'} · {payload.oldPhone || 'без телефона'}
                                <br />
                                Стало: {payload.newRecipientName || 'Без ФИО'} · {payload.newPhone || 'без телефона'}
                                {payload.oldCountry && payload.newCountry && payload.oldCountry !== payload.newCountry ? (
                                  <>
                                    <br />
                                    Страна: {payload.oldCountry} → {payload.newCountry} · Новая сумма: {payload.newCountry === 'KZ' ? payload.newAmountTenge : payload.newAmountSom} {payload.newCountry === 'KZ' ? 'тенге' : 'сом'}
                                  </>
                                ) : null}
                              </div>
                              <div className="mt-1 text-[10px] text-text-muted">
                                Изменил: {item.actor_type === 'admin' ? 'администратор' : 'менеджер'} · ID {item.actor_id}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-text-secondary">Владелец этого трека ещё не менялся.</div>
                    )}
                  </div>
                ) : null}

                {ownerEditorId === order.id ? (
                  <div className="mt-3 rounded-xl border border-neon-yellow/20 bg-neon-yellow/5 p-3">
                    <div className="text-xs font-semibold">Новый владелец трека</div>
                    <div className="mt-1 text-[11px] leading-relaxed text-text-secondary">
                      Найдите зарегистрированного клиента по телефону, ФИО или Telegram ID. При переносе в другую страну потребуется новая сумма в местной валюте.
                    </div>
                    <form onSubmit={(event) => searchOwnerCandidates(event, order.id)} className="mt-3 flex gap-2">
                      <input
                        value={ownerQuery}
                        onChange={(event) => {
                          setOwnerQuery(event.target.value);
                          setOwnerSearched(false);
                          setOwnerCandidates([]);
                          setOwnerError('');
                        }}
                        placeholder="Телефон, ФИО или Telegram ID"
                        className="h-10 min-w-0 flex-1 rounded-xl border border-stroke bg-[#161616] px-3 text-[14px] outline-none focus:border-neon-yellow/50"
                      />
                      <button
                        type="submit"
                        disabled={ownerLoading || ownerQuery.trim().length < 3}
                        className="h-10 rounded-xl bg-neon-yellow px-4 text-xs font-bold text-black disabled:opacity-50"
                      >
                        Найти
                      </button>
                    </form>
                    {ownerError ? <div className="mt-2 text-xs text-red-300">{ownerError}</div> : null}
                    {ownerSearched && !ownerLoading && !ownerCandidates.length && !ownerError ? (
                      <div className="mt-2 text-xs text-text-secondary">Клиенты не найдены.</div>
                    ) : null}
                    {ownerCandidates.length ? (
                      <div className="mt-3 space-y-2">
                        {ownerCandidates.map((candidate) => (
                          <div key={candidate.telegram_id} className="rounded-xl border border-stroke bg-black/15 p-3">
                            <div className="text-sm font-semibold">
                              {candidate.full_name || candidate.nickname || [candidate.first_name, candidate.last_name].filter(Boolean).join(' ') || 'Без ФИО'}
                            </div>
                            <div className="mt-1 text-xs text-text-secondary">{candidate.phone ? `+${String(candidate.phone).replace(/^\+/, '')}` : 'Телефон не указан'}</div>
                            <div className="mt-1 text-[11px] text-text-muted">
                              Telegram ID: {candidate.telegram_id} · Страна: {candidate.country || 'не указана'}
                            </div>
                            {!candidate.country_matches ? (
                              <div className="mt-2 rounded-lg border border-amber-400/25 bg-amber-400/10 px-2 py-2 text-[11px] text-amber-100">
                                Страна заказа изменится с {order.country} на {candidate.country}. Старая сумма в местной валюте будет очищена.
                                {requiresNewLocalAmount(order) ? (
                                  <label className="mt-2 block">
                                    <span className="block text-[10px] uppercase tracking-wide text-amber-100/75">
                                      Новая сумма, {candidate.country === 'KZ' ? 'тенге' : 'сом'}
                                    </span>
                                    <input
                                      value={ownerAmounts[candidate.telegram_id] || ''}
                                      onChange={(event) => {
                                        setOwnerAmounts((current) => ({ ...current, [candidate.telegram_id]: event.target.value }));
                                        setOwnerError('');
                                      }}
                                      inputMode="decimal"
                                      placeholder="Введите сумму"
                                      className="mt-1 h-10 w-full rounded-xl border border-amber-300/25 bg-[#161616] px-3 text-[14px] text-text-primary outline-none focus:border-amber-300/60"
                                    />
                                  </label>
                                ) : (
                                  <div className="mt-2 text-amber-100/75">
                                    Сумма пока не требуется. Её укажут позже на стадии «Готов к выдаче».
                                  </div>
                                )}
                              </div>
                            ) : null}
                            <button
                              type="button"
                              disabled={ownerLoading || (!candidate.country_matches && requiresNewLocalAmount(order) && !parsePositiveAmount(ownerAmounts[candidate.telegram_id]))}
                              onClick={() => changeOwner(order, candidate)}
                              className="mt-2 h-9 w-full rounded-xl border border-neon-yellow/30 bg-neon-yellow/10 text-xs font-semibold text-neon-yellow disabled:opacity-50"
                            >
                              Перенести этому клиенту
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

        {searched && !loading && !orders.length ? (
          <div className="mt-4 rounded-2xl border border-stroke bg-surface p-6 text-center text-sm text-text-secondary">Заказы не найдены.</div>
        ) : null}
      </div>
    </div>
  );
};

export default ManagerOrderSearchPage;
